import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";

import { ThemeProvider, createTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import Logo from "../Images/Logo.png";

function Navbar() {
  const { t, i18n } = useTranslation();
  const [darkMode] = useState(() => {
    return localStorage.getItem("darkMode") === "true";
  });

  useEffect(() => {
    localStorage.setItem("darkMode", darkMode);
  }, [darkMode]);

  const theme = createTheme({
    palette: {
      mode: darkMode ? "dark" : "light",
      primary: {
        main: darkMode ? "#90caf9" : "#1976d2",
      },
      background: {
        default: darkMode ? "#121212" : "#ffffff",
        paper: darkMode ? "#1e1e1e" : "#f5f5f5",
      },
      text: {
        primary: darkMode ? "#ffffff" : "#000000",
      },
    },
    direction: i18n.dir(i18n.language),
  });

  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [open, setOpen] = useState(false);

  const toggleDrawer = (state) => () => {
    setOpen(state);
  };

  const handleNavigation = (path) => {
    // Close the drawer immediately
    setOpen(false);

    // Add a small delay to allow the drawer to close before scrolling
    setTimeout(() => {
      const element = document.getElementById(path.substring(1));
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }, 200); // 200ms delay, adjust if needed
  };

  const navLinks = [
    { text: t("About Us"), path: "#AboutUs" },
    { text: t("Services"), path: "#services" },
    { text: t("Our Approach"), path: "#OurApproach" },
    { text: t("Why Choose Us"), path: "#WhyChooseUs" },
    { text: t("Proposed Solution"), path: "#ProposedSolution" },
    { text: t("Contact Us"), path: "#ContactUs" },
  ];

  return (
    <ThemeProvider theme={theme}>
      <AppBar
        position="sticky"
        sx={{
          backgroundColor: darkMode
            ? "rgba(20, 20, 20, 0.7)"
            : "rgba(255, 255, 255, 0.7)",
          boxShadow: "0 4px 15px rgba(0,0,0,0.1)",
          backdropFilter: "blur(10px)",
          borderBottom: darkMode
            ? "1px solid rgba(255,255,255,0.1)"
            : "1px solid rgba(0,0,0,0.05)",
        }}
      >
        <Toolbar sx={{ justifyContent: "space-between", py: 1.0 }}>
          {/* Logo with a link to the home page */}
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <a
              href="#home"
              onClick={(e) => {
                e.preventDefault();
                handleNavigation("#home");
              }}
              style={{ textDecoration: "none" }}
            >
              <img
                src={Logo}
                alt={t("TechTopia Logo")}
                width={50}
                height={50}
                style={{
                  borderRadius: "10px",
                  transition: "transform 0.3s ease, box-shadow 0.3s ease",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                  "&:hover": {
                    transform: "scale(1.05)",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
                  },
                }}
              />
            </a>
            <Typography
              variant="h6"
              sx={{
                ml: 2,
                color: darkMode ? "#ffffff" : "#000000",
                fontSize: isMobile ? "1.3rem" : "1.6rem",
                fontWeight: 800,
                letterSpacing: "0.8px",
                textTransform: "uppercase",
                background: darkMode
                  ? "linear-gradient(45deg, #e0e0e0 30%, #ffffff 90%)"
                  : "linear-gradient(45deg, #1976d2 30%, #2196f3 90%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              {t("TechTopia")}
            </Typography>
          </Box>

          {/* Desktop Navigation */}
          {!isMobile ? (
            <Box
              sx={{
                display: "flex",
                gap: 3,
                alignItems: "center",
              }}
            >
              {navLinks.map(({ text, path }) => (
                <Button
                  key={text}
                  onClick={() => handleNavigation(path)}
                  sx={{
                    color: darkMode ? "#e0e0e0" : "#333333",
                    cursor: "pointer",
                    fontSize: "1.05rem",
                    fontWeight: 600,
                    textTransform: "none",
                    position: "relative",
                    padding: "8px 12px",
                    borderRadius: "6px",
                    transition: "all 0.3s ease",
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      width: "0%",
                      height: "2px",
                      bottom: "-4px",
                      left: "50%",
                      backgroundColor: darkMode ? "#90caf9" : "#1976d2",
                      transition: "all 0.3s ease",
                      transform: "translateX(-50%)",
                    },
                    "&:hover": {
                      backgroundColor: darkMode
                        ? "rgba(255, 255, 255, 0.08)"
                        : "rgba(0, 0, 0, 0.03)",
                      color: darkMode ? "#ffffff" : "#000000",
                    },
                    "&:hover::after": {
                      width: "100%",
                    },
                  }}
                >
                  {text}
                </Button>
              ))}
            </Box>
          ) : (
            // Mobile Navigation
            <>
              <IconButton
                color="inherit"
                onClick={toggleDrawer(true)}
                sx={{
                  color: darkMode ? "#ffffff" : "#000000",
                  "&:hover": {
                    backgroundColor: darkMode
                      ? "rgba(255, 255, 255, 0.1)"
                      : "rgba(0, 0, 0, 0.05)",
                  },
                }}
              >
                <MenuIcon />
              </IconButton>
              <Drawer
                anchor="right"
                open={open}
                onClose={toggleDrawer(false)}
                PaperProps={{
                  sx: {
                    width: "85%",
                    maxWidth: 350,
                    bgcolor: darkMode ? "#121212" : "#ffffff",
                    boxShadow: "0 0 25px rgba(0,0,0,0.3)",
                    borderLeft: darkMode
                      ? "1px solid rgba(255,255,255,0.1)"
                      : "1px solid rgba(0,0,0,0.1)",
                  },
                }}
              >
                <Box
                  sx={{
                    p: 3,
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    background: darkMode
                      ? "linear-gradient(180deg, rgba(18,18,18,1) 0%, rgba(30,30,30,1) 100%)"
                      : "linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(245,245,245,1) 100%)",
                  }}
                >
                  {/* Drawer Header */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      mb: 3,
                      p: 2,
                      borderRadius: "12px",
                      background: darkMode
                        ? "rgba(255,255,255,0.05)"
                        : "rgba(0,0,0,0.02)",
                      boxShadow: darkMode
                        ? "0 4px 20px rgba(0,0,0,0.2)"
                        : "0 4px 20px rgba(0,0,0,0.05)",
                    }}
                  >
                    <img
                      src={Logo}
                      alt={t("TechTopia Logo")}
                      width={45}
                      height={45}
                      style={{
                        borderRadius: "12px",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                      }}
                    />
                    <Typography
                      variant="h6"
                      sx={{
                        ml: 2,
                        fontWeight: 800,
                        fontSize: "1.5rem",
                        letterSpacing: "0.8px",
                        textTransform: "uppercase",
                        background: darkMode
                          ? "linear-gradient(45deg, #e0e0e0 30%, #ffffff 90%)"
                          : "linear-gradient(45deg, #1976d2 30%, #2196f3 90%)",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                      }}
                    >
                      {t("TechTopia")}
                    </Typography>
                  </Box>

                  <Divider
                    sx={{
                      my: 2,
                      borderColor: darkMode
                        ? "rgba(255,255,255,0.1)"
                        : "rgba(0,0,0,0.1)",
                      opacity: 0.5,
                    }}
                  />

                  {/* Navigation Links */}
                  <List sx={{ flexGrow: 1 }}>
                    {navLinks.map(({ text, path }) => (
                      <ListItem key={text} disablePadding>
                        <ListItemButton
                          onClick={() => handleNavigation(path)}
                          sx={{
                            borderRadius: "12px",
                            my: 1,
                            py: 1.5,
                            px: 2,
                            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                            background: darkMode
                              ? "rgba(255,255,255,0.03)"
                              : "rgba(0,0,0,0.02)",
                            "&:hover": {
                              backgroundColor: darkMode
                                ? "rgba(255,255,255,0.08)"
                                : "rgba(0,0,0,0.04)",
                              boxShadow: darkMode
                                ? "0 4px 20px rgba(0,0,0,0.3)"
                                : "0 4px 20px rgba(0,0,0,0.1)",
                              transform: "translateY(-2px)",
                              "& .MuiListItemText-primary": {
                                color: darkMode ? "#90caf9" : "#1976d2",
                              },
                            },
                          }}
                        >
                          <ListItemText
                            primary={text}
                            primaryTypographyProps={{
                              sx: {
                                fontWeight: 600,
                                fontSize: "1.1rem",
                                color: darkMode ? "#ffffff" : "#000000",
                                transition: "color 0.3s ease",
                              },
                            }}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </Drawer>
            </>
          )}
        </Toolbar>
      </AppBar>
    </ThemeProvider>
  );
}

export default Navbar;
